import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { navigateWithFrom } from '../utils/navigationUtils';
import { navigateToManualServiceRecordCreate } from '../utils/serviceRecordCreateNavigation';
import { sanitizeString, sanitizeSearchInput } from '../utils/security';
import {
  fetchServiceRecords,
  fetchAcikDurumOzet,
  deleteTaslakServisKaydi,
  revertRandevuSuresiGecmis,
  toggleManuelDogrulama,
} from '../services/serviceRecordsService';
import { fetchPanelSetting } from '../services/settingsService';
import TeknikServisDegistirModal from '../components/TeknikServisDegistirModal';
import ServisDurumFilterPills from '../components/ServisDurumFilterPills';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import { getSantralTabLabel } from '../utils/santralTabLabels';
import { getOnemRowClass } from '../utils/onemDerecesiHelper';
import {
  PAGE_DEFAULT_LIMIT,
  SERVICE_RECORD_ROWS_PER_PAGE_OPTIONS,
} from '../constants/pagination';
import { getServiceRecordStatusColor } from '../constants/serviceRecordStatusColors';

const safeText = (value) => sanitizeString(value ?? '');

// Safe date parsing ve formatting
const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  
  try {
    let date;
    
    // MongoDB object format: { $date: '2024-01-01T00:00:00.000Z' }
    if (dateValue && typeof dateValue === 'object' && dateValue.$date) {
      date = new Date(dateValue.$date);
    } 
    // ISO string
    else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    }
    // Timestamp (number)
    else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    }
    // Zaten Date objesi
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    // Object ama başka bir format, toString dene
    else if (typeof dateValue === 'object' && dateValue !== null) {
      // Eğer obje içinde date bilgisi varsa
      if (dateValue.toISOString) {
        date = dateValue;
      } else if (dateValue.toString && dateValue.toString() !== '[object Object]') {
        date = new Date(dateValue.toString());
      } else {
        console.warn('[formatDateTime] Unknown date format:', dateValue);
        return '—';
      }
    }
    else {
      return '—';
    }
    
    // Geçersiz tarih kontrolü
    if (!date || isNaN(date.getTime())) {
      console.warn('[formatDateTime] Invalid date:', dateValue);
      return '—';
    }
    
    // Türkçe format
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    console.error('[formatDateTime] Error:', e, dateValue);
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const SERVICE_TAB_IDS = ['acik', 'tamamlanan', 'randevuBekleniyor', 'taslaklar'];

const withoutTaslakKayitlari = (list) => (
  Array.isArray(list) ? list.filter((r) => String(r?.durum || '').trim() !== 'Taslak') : []
);

const ServiceRecordsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { confirm } = useConfirmDialog();
  const userRol = (user?.rol || '').toLowerCase();
  const isAdmin = userRol === 'admin';
  const isSales = userRol === 'sales';
  const canChangeTsOnOpenRecord = (isAdmin || isSales);
  const urlInitRef = useRef(false);
  const tabFromUrl = searchParams.get('tab');
  const initialTab = SERVICE_TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'acik';
  const aramaFromUrl = searchParams.get('arama') || '';

  // Sekme yönetimi
  const [activeTab, setActiveTab] = useState(initialTab); // 'acik' | 'tamamlanan' | 'randevuBekleniyor'
  
  // Üç ayrı tablo için state'ler
  const [acikRecords, setAcikRecords] = useState([]);
  const [tamamlananRecords, setTamamlananRecords] = useState([]);
  const [randevuBekleniyorRecords, setRandevuBekleniyorRecords] = useState([]);
  const [taslakRecords, setTaslakRecords] = useState([]);
  
  // Üç ayrı pagination
  const [acikPage, setAcikPage] = useState(1);
  const [tamamlananPage, setTamamlananPage] = useState(1);
  const [randevuBekleniyorPage, setRandevuBekleniyorPage] = useState(1);
  const [acikRowsPerPage, setAcikRowsPerPage] = useState(PAGE_DEFAULT_LIMIT);
  const [tamamlananRowsPerPage, setTamamlananRowsPerPage] = useState(PAGE_DEFAULT_LIMIT);
  const [randevuBekleniyorRowsPerPage, setRandevuBekleniyorRowsPerPage] = useState(PAGE_DEFAULT_LIMIT);
  const [acikPaginationMeta, setAcikPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [tamamlananPaginationMeta, setTamamlananPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [randevuBekleniyorPaginationMeta, setRandevuBekleniyorPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [taslakPage, setTaslakPage] = useState(1);
  const [taslakRowsPerPage, setTaslakRowsPerPage] = useState(PAGE_DEFAULT_LIMIT);
  const [taslakPaginationMeta, setTaslakPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const [acikDurumFilter, setAcikDurumFilter] = useState([]);
  const [acikDurumOzet, setAcikDurumOzet] = useState([]);
  const [durumOzetLoading, setDurumOzetLoading] = useState(false);
  
  const [filters, setFilters] = useState({ search: aramaFromUrl, islemTipi: '' });
  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');

  const [durumDegistirRecordId, setDurumDegistirRecordId] = useState(null);
  const [durumDegistirNot, setDurumDegistirNot] = useState('');
  const [durumDegistirLoading, setDurumDegistirLoading] = useState(false);
  const [durumDegistirError, setDurumDegistirError] = useState('');

  const [manuelModal, setManuelModal] = useState(null);
  const [manuelNot, setManuelNot] = useState('');
  const [manuelLoading, setManuelLoading] = useState(false);
  const [manuelError, setManuelError] = useState('');

  const [tsDegistirModal, setTsDegistirModal] = useState(null);

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const filterParams = useMemo(
    () => ({
      search: sanitizedSearchValue,
      islemTipi: filters.islemTipi || undefined,
    }),
    [sanitizedSearchValue, filters.islemTipi]
  );

  // Açık kayıtları yükle (Durumu "Tamamlandı" olmayanlar)
  const loadAcikRecords = useCallback(async () => {
    if (activeTab !== 'acik') return; // Sadece açık sekme aktifse yükle
    
    setLoading(true);
    setError('');
    try {
      const requestParams = {
        page: acikPage,
        limit: acikRowsPerPage,
        aktif: true,
        search: filterParams.search,
        islemTipi: filterParams.islemTipi,
        islemTipiHaric: 'İstek ve Şikayet',
        ...(acikDurumFilter.length > 0
          ? { durumlar: acikDurumFilter }
          : { durum: 'Tamamlandı_Degil' }),
      };
      
      const response = await fetchServiceRecords(requestParams);
      const list = withoutTaslakKayitlari(response?.veri?.servisKayitlari || response?.veri || []);
      const pagination = response?.veri?.sayfalama || {};
      
      // Önem derecesine göre sırala
      const sorted = sortByPriority(list);
      setAcikRecords(sorted);
      setAcikPaginationMeta({
        total: pagination.toplam || list.length,
        totalPages: pagination.toplamSayfa || Math.ceil(list.length / acikRowsPerPage) || 1,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Açık kayıtlar yüklenirken hata oluştu'));
      setAcikRecords([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [acikPage, acikRowsPerPage, filterParams, activeTab, acikDurumFilter]);

  const loadAcikDurumOzet = useCallback(async () => {
    if (activeTab !== 'acik') return;
    setDurumOzetLoading(true);
    try {
      const response = await fetchAcikDurumOzet({
        search: filterParams.search,
        islemTipi: filterParams.islemTipi,
      });
      setAcikDurumOzet(response?.veri?.kalemler || []);
    } catch {
      setAcikDurumOzet([]);
    } finally {
      setDurumOzetLoading(false);
    }
  }, [activeTab, filterParams]);

  // Tamamlanan kayıtları yükle (Durumu "Tamamlandı" olanlar)
  const loadTamamlananRecords = useCallback(async () => {
    if (activeTab !== 'tamamlanan') return; // Sadece tamamlanan sekme aktifse yükle
    
    setLoading(true);
    setError('');
    try {
      const requestParams = {
        page: tamamlananPage,
        limit: tamamlananRowsPerPage,
        aktif: true,
        ...filterParams,
        islemTipiHaric: 'İstek ve Şikayet',
        durum: 'Tamamlandı', // Sadece tamamlananları getir
      };
      
      const response = await fetchServiceRecords(requestParams);
      const list = withoutTaslakKayitlari(response?.veri?.servisKayitlari || response?.veri || []);
      const pagination = response?.veri?.sayfalama || {};
      
      // Önem derecesine göre sırala
      const sorted = sortByPriority(list);
      setTamamlananRecords(sorted);
      setTamamlananPaginationMeta({
        total: pagination.toplam || list.length,
        totalPages: pagination.toplamSayfa || Math.ceil(list.length / tamamlananRowsPerPage) || 1,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Tamamlanan kayıtlar yüklenirken hata oluştu'));
      setTamamlananRecords([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [tamamlananPage, tamamlananRowsPerPage, filterParams, activeTab]);

  // Randevu Bekleniyor kayıtlarını yükle (Durumu "Randevu Bekleniyor" olanlar - en eski en üstte)
  const loadRandevuBekleniyorRecords = useCallback(async () => {
    if (activeTab !== 'randevuBekleniyor') return; // Sadece randevu bekleniyor sekme aktifse yükle
    
    setLoading(true);
    setError('');
    try {
      const requestParams = {
        page: randevuBekleniyorPage,
        limit: randevuBekleniyorRowsPerPage,
        aktif: true,
        ...filterParams,
        islemTipiHaric: 'İstek ve Şikayet',
        durum: 'Randevu Bekleniyor', // Sadece randevu bekleniyor durumundakileri getir
      };
      
      const response = await fetchServiceRecords(requestParams);
      const list = withoutTaslakKayitlari(response?.veri?.servisKayitlari || response?.veri || []);
      const pagination = response?.veri?.sayfalama || {};
      
      // Backend'den zaten oluşturulma tarihine göre artan sıralı geliyor (en eski en üstte)
      setRandevuBekleniyorRecords(list);
      setRandevuBekleniyorPaginationMeta({
        total: pagination.toplam || list.length,
        totalPages: pagination.toplamSayfa || Math.ceil(list.length / randevuBekleniyorRowsPerPage) || 1,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Randevu bekleniyor kayıtlar yüklenirken hata oluştu'));
      setRandevuBekleniyorRecords([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [randevuBekleniyorPage, randevuBekleniyorRowsPerPage, filterParams, activeTab]);

  const loadTaslakRecords = useCallback(async () => {
    if (activeTab !== 'taslaklar') return;

    setLoading(true);
    setError('');
    try {
      const response = await fetchServiceRecords({
        page: taslakPage,
        limit: taslakRowsPerPage,
        aktif: true,
        search: filterParams.search,
        islemTipi: filterParams.islemTipi,
        islemTipiHaric: 'İstek ve Şikayet',
        durum: 'Taslak',
      });
      const list = response?.veri?.servisKayitlari || response?.veri || [];
      const pagination = response?.veri?.sayfalama || {};
      setTaslakRecords(Array.isArray(list) ? list : []);
      setTaslakPaginationMeta({
        total: pagination.toplam || list.length,
        totalPages: pagination.toplamSayfa || Math.ceil(list.length / taslakRowsPerPage) || 1,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Taslak kayıtlar yüklenirken hata oluştu'));
      setTaslakRecords([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [taslakPage, taslakRowsPerPage, filterParams, activeTab]);

  // Aktif sekmeye göre kayıtları yükle
  useEffect(() => {
    if (activeTab === 'acik') {
      loadAcikRecords();
    } else if (activeTab === 'tamamlanan') {
      loadTamamlananRecords();
    } else if (activeTab === 'randevuBekleniyor') {
      loadRandevuBekleniyorRecords();
    } else if (activeTab === 'taslaklar') {
      loadTaslakRecords();
    }
  }, [activeTab, loadAcikRecords, loadTamamlananRecords, loadRandevuBekleniyorRecords, loadTaslakRecords]);

  useEffect(() => {
    if (activeTab === 'acik') {
      loadAcikDurumOzet();
    }
  }, [activeTab, loadAcikDurumOzet]);

  useEffect(() => {
    const available = new Set(acikDurumOzet.map((k) => k.durum));
    setAcikDurumFilter((prev) => prev.filter((d) => available.has(d)));
  }, [acikDurumOzet]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchPanelSetting('SERVIS_KAYDI_LISTE_SATIR');
        const val = Number(response?.veri?.deger);
        if (cancelled || !SERVICE_RECORD_ROWS_PER_PAGE_OPTIONS.includes(val)) return;
        setAcikRowsPerPage(val);
        setTamamlananRowsPerPage(val);
        setRandevuBekleniyorRowsPerPage(val);
        setTaslakRowsPerPage(val);
      } catch {
        /* varsayılan PAGE_DEFAULT_LIMIT */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAcikDurumToggle = (durum) => {
    setAcikDurumFilter((prev) => {
      if (prev.includes(durum)) {
        return prev.filter((d) => d !== durum);
      }
      return [...prev, durum];
    });
    setAcikPage(1);
  };

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value);
  };

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    if (!tabFromUrl || !SERVICE_TAB_IDS.includes(tabFromUrl)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', initialTab);
        if (aramaFromUrl) next.set('arama', aramaFromUrl);
        return next;
      }, { replace: true });
    }
  }, [tabFromUrl, initialTab, aramaFromUrl, setSearchParams]);

  // Debounce için search input
  useEffect(() => {
    if (searchInput.trim().length === 0) {
      setSearchLoading(false);
      setFilters((prev) => ({ ...prev, search: '' }));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('arama');
        return next;
      }, { replace: true });
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchLoading(true);
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setAcikPage(1);
      setTamamlananPage(1);
      setRandevuBekleniyorPage(1);
      setTaslakPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('arama', searchInput.trim());
        return next;
      }, { replace: true });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, setSearchParams]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setAcikPage(1);
    setTamamlananPage(1);
    setRandevuBekleniyorPage(1);
    setTaslakPage(1);
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setAcikPage(1);
    setTamamlananPage(1);
    setRandevuBekleniyorPage(1);
    setTaslakPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };


  const handleTaslakDevam = (recordId) => {
    if (!recordId) return;
    navigate(`/service-records/create?taslakId=${encodeURIComponent(recordId)}`);
  };

  const handleTaslakSil = async (recordId) => {
    if (!recordId || !window.confirm('Kalıcı taslak silinsin mi?')) return;
    try {
      await deleteTaslakServisKaydi(recordId);
      loadTaslakRecords();
    } catch (err) {
      window.alert(getErrorMessage(err, 'Taslak silinemedi'));
    }
  };

  const getOlusturanAdi = (record) => {
    const olusturan = record.olusturanKullanici || record.olusturanKullaniciId;
    if (!olusturan || typeof olusturan !== 'object') return '—';
    const adSoyad = `${olusturan.ad || ''} ${olusturan.soyad || ''}`.trim();
    return adSoyad || olusturan.kullaniciAdi || '—';
  };

  const renderTaslakTable = (records) => (
    <table className="admins-table">
      <thead>
        <tr>
          <th>Müşteri</th>
          <th>İşlem Tipi</th>
          <th>Adım</th>
          <th>Oluşturan</th>
          <th>Son Güncelleme</th>
          <th className="islemler-sutunu">İşlemler</th>
        </tr>
      </thead>
      <tbody>
        {records.map((record) => {
          const recordId = record.id || record._id;
          const customer = record.musteri || {};
          const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—';
          return (
            <tr key={recordId} className={getOnemRowClass(record.onemDerecesi) || undefined}>
              <td>
                <span
                  className="tech-service-name-link"
                  onClick={() => handleTaslakDevam(recordId)}
                  title="Devam et"
                >
                  {customerName}
                </span>
              </td>
              <td>{record.islemTipi || '—'}</td>
              <td>
                <span style={{ fontWeight: 500, color: 'var(--text-gray)' }}>
                  {getSantralTabLabel(record.taslakAdim)}
                </span>
              </td>
              <td>{getOlusturanAdi(record)}</td>
              <td>{formatDateTime(record.updatedAt || record.guncellemeTarihi || record.olusturmaTarihi)}</td>
              <td className="table-actions islemler-sutunu">
                <button
                  type="button"
                  className="category-action-btn edit-btn"
                  onClick={() => handleTaslakDevam(recordId)}
                  title="Devam et"
                >
                  <i className="fas fa-play" />
                </button>
                <button
                  type="button"
                  className="category-action-btn delete-btn"
                  onClick={() => handleTaslakSil(recordId)}
                  title="Sil"
                >
                  <i className="fas fa-trash-alt" />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const getStatusBadge = (durum) => {
    const color = getServiceRecordStatusColor(durum);
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}40`,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}>
        {durum || '—'}
      </span>
    );
  };

  // Önem derecesi sıralaması için priority değerleri
  const priorityOrder = {
    'Acil': 1,
    'Yüksek': 2,
    'Orta': 3,
    'Düşük': 4,
  };

  // Kayıtları önem derecesine göre sırala
  const sortByPriority = (records) => {
    return [...records].sort((a, b) => {
      const priorityA = priorityOrder[a.onemDerecesi] || 999;
      const priorityB = priorityOrder[b.onemDerecesi] || 999;
      return priorityA - priorityB;
    });
  };

  const getTeknikServisAdi = (record) => {
    const ad = record?.aktifAtamaTeknikServis?.ad;
    if (ad && String(ad).trim()) return String(ad).trim();
    return '—';
  };

  const reloadActiveTab = useCallback(() => {
    if (activeTab === 'acik') loadAcikRecords();
    else if (activeTab === 'tamamlanan') loadTamamlananRecords();
    else if (activeTab === 'randevuBekleniyor') loadRandevuBekleniyorRecords();
    else if (activeTab === 'taslaklar') loadTaslakRecords();
  }, [activeTab, loadAcikRecords, loadTamamlananRecords, loadRandevuBekleniyorRecords, loadTaslakRecords]);

  const openDurumDegistirModal = (recordId) => {
    setDurumDegistirRecordId(recordId);
    setDurumDegistirNot('');
    setDurumDegistirError('');
  };

  const closeDurumDegistirModal = () => {
    if (durumDegistirLoading) return;
    setDurumDegistirRecordId(null);
    setDurumDegistirNot('');
    setDurumDegistirError('');
  };

  const handleDurumDegistirSubmit = async () => {
    if (!durumDegistirRecordId) return;
    const trimmed = durumDegistirNot.trim();
    if (trimmed.length < 5) {
      setDurumDegistirError('En az 5 karakterlik bir not yazın');
      return;
    }
    setDurumDegistirLoading(true);
    setDurumDegistirError('');
    try {
      await revertRandevuSuresiGecmis(durumDegistirRecordId, trimmed);
      setDurumDegistirRecordId(null);
      setDurumDegistirNot('');
      setDurumDegistirError('');
      reloadActiveTab();
    } catch (err) {
      setDurumDegistirError(getErrorMessage(err, 'Durum güncellenemedi'));
    } finally {
      setDurumDegistirLoading(false);
    }
  };


  const openManuelModal = (record) => {
    const recordId = record.id || record._id;
    const aktif = record.manuelDogrulamaModu === true;
    setManuelModal({ recordId, aktif, customerName: record.musteri?.adi || '' });
    setManuelNot('');
    setManuelError('');
  };

  const closeManuelModal = () => {
    if (manuelLoading) return;
    setManuelModal(null);
    setManuelNot('');
    setManuelError('');
  };

  const handleManuelSubmit = async () => {
    if (!manuelModal?.recordId) return;
    setManuelLoading(true);
    setManuelError('');
    try {
      const yeniAktif = !manuelModal.aktif;
      await toggleManuelDogrulama(manuelModal.recordId, yeniAktif, manuelNot.trim() || undefined);
      setManuelModal(null);
      setManuelNot('');
      reloadActiveTab();
    } catch (err) {
      setManuelError(getErrorMessage(err, 'Manuel doğrulama güncellenemedi'));
    } finally {
      setManuelLoading(false);
    }
  };

  const openTsDegistirModal = (record) => {
    const recordId = record.id || record._id;
    const currentTs = record.aktifAtamaTeknikServis;
    const currentTsId = currentTs?.id || currentTs?._id || null;
    setTsDegistirModal({
      recordId,
      currentTsId: currentTsId ? String(currentTsId) : null,
      currentTsAd: currentTs?.ad || '—',
      customerName: [
        record.musteri?.adi || record.musteri?.name,
        record.musteri?.soyadi || record.musteri?.surname,
      ].filter(Boolean).join(' ') || '—',
    });
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Acil': '#ef4444',
      'Yüksek': '#f59e0b',
      'Orta': '#3b82f6',
      'Düşük': '#64748b',
    };
    
    const color = priorityColors[priority] || '#64748b';
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}40`
      }}>
        {priority || '—'}
      </span>
    );
  };

  const renderTable = (records, showEmpty = false) => {
    if (showEmpty && records.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '20px', textAlign: 'center', background: 'var(--bg-light)', borderRadius: '8px' }}>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Kayıt bulunamadı</div>
        </div>
      );
    }

    if (records.length === 0) return null;

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Müşteri</th>
            <th>İşlem Tipi</th>
            <th>Teknik Servis</th>
            <th>Önem Derecesi</th>
            <th>Durum</th>
            <th>Randevu Sayısı</th>
            <th>Oluşturma Tarihi</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const recordId = record.id || record._id;
            const customer = record.musteri || {};
            const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—';

            return (
              <tr key={recordId} className={getOnemRowClass(record.onemDerecesi) || undefined}>
                <td>
                  <span
                    className="tech-service-name-link"
                    onClick={() => navigateWithFrom(navigate, `/service-records/${recordId}/update`, location)}
                  >
                    {customerName}
                  </span>
                </td>
                <td>{record.islemTipi || '—'}</td>
                <td>{getTeknikServisAdi(record)}</td>
                <td>{getPriorityBadge(record.onemDerecesi)}</td>
                <td>{getStatusBadge(record.durum)}</td>
                <td>
                  <span style={{ fontWeight: 500 }}>
                    {record.randevuSayisi || 0}
                  </span>
                </td>
                <td>
                  {formatDateTime(record.olusturmaTarihi || record.createdAt)}
                </td>
                <td className="table-actions islemler-sutunu">
                  {activeTab !== 'taslaklar' && activeTab !== 'tamamlanan' && (
                    <button
                      type="button"
                      className="category-action-btn"
                      style={{
                        marginRight: 6,
                        color: record.manuelDogrulamaModu ? '#ea580c' : undefined,
                      }}
                      onClick={() => openManuelModal(record)}
                      title={
                        record.manuelDogrulamaModu
                          ? 'Manuel doğrulama açık'
                          : 'Manuel doğrulama'
                      }
                    >
                      <i className="fas fa-user-shield" />
                    </button>
                  )}
                  {canChangeTsOnOpenRecord &&
                    activeTab !== 'taslaklar' &&
                    activeTab !== 'tamamlanan' &&
                    record.aktifAtamaTeknikServis?.ad &&
                    record.durum !== 'Taslak' &&
                    record.durum !== 'Tamamlandı' && (
                    <button
                      type="button"
                      className="category-action-btn"
                      style={{ marginRight: 6, color: '#2563eb' }}
                      onClick={() => openTsDegistirModal(record)}
                      title="Teknik Servis Atamasını Değiştir"
                    >
                      <i className="fas fa-building" />
                    </button>
                  )}
                  {record.durum === 'Randevu Süresi Geçmiş' && (
                    <button
                      type="button"
                      className="category-action-btn"
                      style={{ marginRight: 6, color: '#dc2626' }}
                      onClick={() => openDurumDegistirModal(recordId)}
                      title="Durum Değiştir"
                    >
                      <i className="fas fa-exchange-alt"></i>
                    </button>
                  )}
                  {(activeTab !== 'tamamlanan' || !isSales) && (
                    <button
                      type="button"
                      className="category-action-btn edit-btn"
                      onClick={() => navigateWithFrom(navigate, `/service-records/${recordId}/update`, location)}
                      title={activeTab === 'tamamlanan' && isSales ? 'Tamamlanan kayıt güncellenemez' : 'Güncelle'}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Ana render fonksiyonu - Sekme tabanlı tablo
  const renderServiceRecordsTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Servis kayıtları yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }
    if (error) return <div className="error-state">{safeText(error)}</div>;
    
    const currentRecords = activeTab === 'acik'
      ? acikRecords
      : activeTab === 'tamamlanan'
        ? tamamlananRecords
        : activeTab === 'taslaklar'
          ? taslakRecords
          : randevuBekleniyorRecords;
    const currentPaginationMeta = activeTab === 'acik'
      ? acikPaginationMeta
      : activeTab === 'tamamlanan'
        ? tamamlananPaginationMeta
        : activeTab === 'taslaklar'
          ? taslakPaginationMeta
          : randevuBekleniyorPaginationMeta;
    const currentPage = activeTab === 'acik'
      ? acikPage
      : activeTab === 'tamamlanan'
        ? tamamlananPage
        : activeTab === 'taslaklar'
          ? taslakPage
          : randevuBekleniyorPage;
    const setCurrentPage = activeTab === 'acik'
      ? setAcikPage
      : activeTab === 'tamamlanan'
        ? setTamamlananPage
        : activeTab === 'taslaklar'
          ? setTaslakPage
          : setRandevuBekleniyorPage;
    const currentRowsPerPage = activeTab === 'acik'
      ? acikRowsPerPage
      : activeTab === 'tamamlanan'
        ? tamamlananRowsPerPage
        : activeTab === 'taslaklar'
          ? taslakRowsPerPage
          : randevuBekleniyorRowsPerPage;
    const handleRowsPerPageChange = (value) => {
      if (!SERVICE_RECORD_ROWS_PER_PAGE_OPTIONS.includes(value)) return;
      if (activeTab === 'acik') {
        setAcikRowsPerPage(value);
        setAcikPage(1);
      } else if (activeTab === 'tamamlanan') {
        setTamamlananRowsPerPage(value);
        setTamamlananPage(1);
      } else if (activeTab === 'taslaklar') {
        setTaslakRowsPerPage(value);
        setTaslakPage(1);
      } else {
        setRandevuBekleniyorRowsPerPage(value);
        setRandevuBekleniyorPage(1);
      }
    };
    
    if (currentRecords.length === 0 && !loading) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-clipboard-list" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
            {activeTab === 'taslaklar'
              ? 'Kalıcı taslak bulunamadı'
              : activeTab === 'randevuBekleniyor'
                ? 'Randevu bekleniyor durumunda servis kaydı bulunamadı'
                : activeTab === 'acik'
                  ? (acikDurumFilter.length > 0
                    ? 'Seçili durumlarda servis kaydı bulunamadı'
                    : 'Açık servis kaydı bulunamadı')
                  : 'Tamamlanan servis kaydı bulunamadı'}
          </div>
        </div>
      );
    }

    return (
      <div style={{
        background: '#fff',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ overflow: 'hidden' }}>
          {activeTab === 'taslaklar' ? renderTaslakTable(currentRecords) : renderTable(currentRecords, true)}
        </div>
        
        <TableListFooter
          totalRecords={currentPaginationMeta.total}
          currentPage={currentPage}
          totalPages={currentPaginationMeta.totalPages || 1}
          onPageChange={setCurrentPage}
          loading={loading}
          rowsPerPage={currentRowsPerPage}
          rowsPerPageOptions={SERVICE_RECORD_ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={handleRowsPerPageChange}
          embedded
        />
      </div>
    );
  };

  return (
    <main className="main-content service-records">
      <div className="content-header">
        <h1>Servis Kaydı Takip</h1>
      </div>

      {/* Sekmeler */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: '2px solid var(--border-color)',
        paddingBottom: '0'
      }}>
        <button
          type="button"
          onClick={() => handleTabChange('randevuBekleniyor')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'randevuBekleniyor' ? '2px solid #f59e0b' : '2px solid transparent',
            background: 'transparent',
            color: activeTab === 'randevuBekleniyor' ? '#f59e0b' : 'var(--text-gray)',
            fontSize: '14px',
            fontWeight: activeTab === 'randevuBekleniyor' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          <i className="fas fa-clock" style={{ marginRight: '8px' }}></i>
          Randevu Bekleniyor
          {activeTab === 'randevuBekleniyor' && randevuBekleniyorRecords.length > 0 && (
            <span style={{
              marginLeft: '8px',
              padding: '2px 8px',
              borderRadius: '12px',
              background: '#f59e0b',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {randevuBekleniyorPaginationMeta.total}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('acik')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'acik' ? '2px solid #3b82f6' : '2px solid transparent',
            background: 'transparent',
            color: activeTab === 'acik' ? '#3b82f6' : 'var(--text-gray)',
            fontSize: '14px',
            fontWeight: activeTab === 'acik' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          <i className="fas fa-folder-open" style={{ marginRight: '8px' }}></i>
          Açık Kayıtlar
          {activeTab === 'acik' && acikRecords.length > 0 && (
            <span style={{
              marginLeft: '8px',
              padding: '2px 8px',
              borderRadius: '12px',
              background: '#3b82f6',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {acikPaginationMeta.total}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('tamamlanan')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'tamamlanan' ? '2px solid #059669' : '2px solid transparent',
            background: 'transparent',
            color: activeTab === 'tamamlanan' ? '#059669' : 'var(--text-gray)',
            fontSize: '14px',
            fontWeight: activeTab === 'tamamlanan' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
          Tamamlanan Kayıtlar
          {activeTab === 'tamamlanan' && tamamlananRecords.length > 0 && (
            <span style={{
              marginLeft: '8px',
              padding: '2px 8px',
              borderRadius: '12px',
              background: '#059669',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {tamamlananPaginationMeta.total}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('taslaklar')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'taslaklar' ? '2px solid #64748b' : '2px solid transparent',
            background: 'transparent',
            color: activeTab === 'taslaklar' ? '#64748b' : 'var(--text-gray)',
            fontSize: '14px',
            fontWeight: activeTab === 'taslaklar' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          <i className="fas fa-file-alt" style={{ marginRight: '8px' }}></i>
          Taslaklar
          {activeTab === 'taslaklar' && taslakRecords.length > 0 && (
            <span style={{
              marginLeft: '8px',
              padding: '2px 8px',
              borderRadius: '12px',
              background: '#64748b',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {taslakPaginationMeta.total}
            </span>
          )}
        </button>
      </div>

      <div className="toolbar">
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div className="search-filter-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: '250px' }}>
            {searchLoading ? (
              <span style={{ 
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}>
                <i className="fas fa-spinner fa-spin" style={{ 
                  color: 'var(--text-gray)'
                }}></i>
              </span>
            ) : (
              <i className="fas fa-search"></i>
            )}
            <input
              type="text"
              placeholder="Müşteri adı, telefon, servis no, teknik servis"
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
            />
          </div>
          
          <select
            value={filters.islemTipi}
            onChange={(e) => handleFilterChange('islemTipi', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '150px'
            }}
          >
            <option value="">Tüm İşlem Tipleri</option>
            <option value="Kurulum">Kurulum</option>
            <option value="Bakım">Bakım</option>
            <option value="Arıza">Arıza</option>
          </select>
        </div>

        {activeTab === 'acik' && (
          <ServisDurumFilterPills
            kalemler={acikDurumOzet}
            selectedDurumlar={acikDurumFilter}
            onToggle={handleAcikDurumToggle}
            loading={durumOzetLoading}
          />
        )}
        </div>

        <button 
          type="button" 
          className="primary-btn add-user-btn" 
          onClick={() => navigateToManualServiceRecordCreate(navigate)}
        >
          <i className="fas fa-plus"></i>
          <span>Yeni Servis Kaydı</span>
        </button>
      </div>

      <div className="table-container" style={{ background: 'transparent', border: 'none' }}>
        {renderServiceRecordsTable()}
      </div>

      <TeknikServisDegistirModal
        isOpen={Boolean(tsDegistirModal)}
        recordId={tsDegistirModal?.recordId}
        customerName={tsDegistirModal?.customerName}
        currentTsId={tsDegistirModal?.currentTsId}
        currentTsAd={tsDegistirModal?.currentTsAd}
        onClose={() => setTsDegistirModal(null)}
        onSuccess={reloadActiveTab}
      />

      {manuelModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={closeManuelModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>
              {manuelModal.aktif ? 'Manuel doğrulamayı kapat' : 'Manuel doğrulama aç'}
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-gray)' }}>
              {manuelModal.aktif
                ? 'Teknisyen tamamlamada konum ve OTP otomatik onaylanmaz.'
                : 'Teknisyen tamamlamada konum ve OTP otomatik onaylanır.'}
            </p>
            {!manuelModal.aktif && (
              <>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                  Not (isteğe bağlı)
                </label>
                <textarea
                  value={manuelNot}
                  onChange={(e) => setManuelNot(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Örn. Saha internet/konum sorunu"
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 8,
                    border: '1px solid var(--border-color)',
                    fontSize: 14,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </>
            )}
            {manuelError && (
              <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{manuelError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="secondary-btn" onClick={closeManuelModal} disabled={manuelLoading}>
                İptal
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleManuelSubmit}
                disabled={manuelLoading}
              >
                {manuelLoading ? 'Kaydediliyor…' : manuelModal.aktif ? 'Kapat' : 'Aç'}
              </button>
            </div>
          </div>
        </div>
      )}

      {durumDegistirRecordId && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={closeDurumDegistirModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>Durum Değiştir</h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-gray)' }}>
              Randevu Bekleniyor durumuna alınır; süresi geçmiş randevu iptal edilir.
            </p>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
              Not
            </label>
            <textarea
              value={durumDegistirNot}
              onChange={(e) => setDurumDegistirNot(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Örn. Teknisyen randevuyu unutmuş"
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 8,
                border: '1px solid var(--border-color)',
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            {durumDegistirError && (
              <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{durumDegistirError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="secondary-btn" onClick={closeDurumDegistirModal} disabled={durumDegistirLoading}>
                İptal
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleDurumDegistirSubmit}
                disabled={durumDegistirLoading || durumDegistirNot.trim().length < 5}
              >
                {durumDegistirLoading ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ServiceRecordsPage;


